import { PermissionType } from 'domain/_entities/permission.entity'
import { useUserContext } from 'domain/users/user_provider/user.provider'
import { RouteDefs } from 'routes/_utils/transform_routes/transform.routes'

const HOME_LAYOUT = () => import('presentation/home_layout/home_layout')
const PROJECT_SEARCH_PAGE = () =>
  import('presentation/projects/project_search_page/project_search_page')
const COMPANY_PAGE_LAYOUT = () =>
  import('presentation/company/_company_page_layout/company_page_layout.view')
const MANAGE_COMPANY = () =>
  import('presentation/company/delivery_evaluations/delivery_evaluations.view')
const INVOICE_EVALUATIONS = () =>
  import('presentation/company/invoice_evaluations/invoice_evaluations.view')
const INVOICE_POSITION_EVALUATIONS = () =>
  import(
    'presentation/company/invoice_position_evaluations/invoice_position_evaluations.view'
  )
const PRODUCT_MAPPING = () =>
  import('presentation/company/product_mapping/product_mapping.view')
const INVOICE_RECEIPT = () =>
  import('presentation/invoices/invoice_receipt/invoice_receipt.view')
const ADMIN_PAGE_LAYOUT = () => import('presentation/layout/admin/admin_layout')
const MANAGE_PROJECTS_VIEW = () =>
  import('presentation/projects/manage_projects/manage_projects.view')
const MANAGE_USERS_PAGE = () =>
  import('presentation/users/manage_users/manage_users.view')
const MANAGE_ROLES_PAGE = () =>
  import('presentation/roles/manage_roles/manage_roles.view')
const QUALITY_CONTROL = () =>
  import('presentation/customer_admin/quality_control/quality_control.view')
const MAPPINGS_PAGE = () =>
  import('presentation/projects/project_mappings/project_mappings.view')
const INVOICE_SETTINGS_PAGE = () =>
  import('presentation/customer_admin/invoice_settings/invoice_settings.view')
const INVOICE_MATCH = () =>
  import('presentation/invoices/invoice_matching/invoice_matching.view')
const SUPER_ADMIN_OVERVIEW = () =>
  import(
    'presentation/customer_admin/super_admin_overview/super_admin_overview.view'
  )
const MARKINGS_PAGE = () =>
  import('presentation/deliveries/delivery_markings/delivery_markings.view')
const LOGINS_PAGE = () =>
  import('presentation/support/login_analytics/logins_page')
const MANAGE_SUPPLIERS_PAGE_SUPER_ADMIN = () =>
  import('presentation/suppliers/manage_suppliers/manage_suppliers_admin.view')
const MANAGE_SUPPLIERS_PAGE = () =>
  import('presentation/suppliers/manage_suppliers/manage_supplier.view')
const MANAGE_SUPPLIER_DETAILS_PAGE = () =>
  import(
    'presentation/suppliers/manage_suppliers/details/manage_supplier_details.view'
  )

const ORDERS_PAGE = () =>
  import('presentation/customer_admin/order_monitoring/orders_page')
const MANAGE_PROJECT_REGIONS_VIEW = () =>
  import(
    'presentation/project_regions/manage_project_regions/manage_project_regions.view'
  )
const MANAGE_API_KEYS_VIEW = () =>
  import(
    'presentation/customer_admin/api_key_management/api_key_management.view'
  )
const INVOICE_EDIT_LAYOUT = () =>
  import('presentation/invoices/_shared/layout/invoice_edit_layout')
const INVOICE_EDIT_WITH_URL = () =>
  import(
    'presentation/invoices/invoice_edit_with_url/invoice_edit_with_url.view'
  )

/**
 * Route Definitions of the Home Routes for the Admin
 * @category Routes
 */
export const HOME_ROUTES_ADMIN: RouteDefs[] = [
  {
    path: 'super-admin',
    file: ADMIN_PAGE_LAYOUT,
    children: [
      {
        index: true,
        file: SUPER_ADMIN_OVERVIEW,
      },
      {
        path: 'projects',
        file: MANAGE_PROJECTS_VIEW,
      },
      {
        path: 'markings',
        file: MARKINGS_PAGE,
      },
      {
        path: 'logins',
        file: LOGINS_PAGE,
      },
      {
        path: 'suppliers',
        file: MANAGE_SUPPLIERS_PAGE_SUPER_ADMIN,
      },
      {
        path: 'quality-control',
        file: QUALITY_CONTROL,
      },
      {
        path: 'api-keys',
        file: MANAGE_API_KEYS_VIEW,
      },
    ],
  },
  {
    path: 'monitoring',
    file: ADMIN_PAGE_LAYOUT,
    children: [
      {
        index: true,
        file: ORDERS_PAGE,
      },
      {
        path: 'orders',
        file: ORDERS_PAGE,
      },
    ],
  },
]

/**
 * Route Definitions for the Home Routes
 * @category Routes
 */
const HOME_ROUTES = (isAdmin: boolean): RouteDefs[] => [
  {
    path: '/',
    file: HOME_LAYOUT,
    children: [
      {
        index: true,
        file: PROJECT_SEARCH_PAGE,
      },
      {
        path: 'company',
        file: COMPANY_PAGE_LAYOUT,
        children: [
          {
            path: 'delivery-evaluations',
            file: MANAGE_COMPANY,
            permissions: [PermissionType.EDIT_ALL_DELIVERIES],
          },
          {
            path: 'invoice-evaluations',
            file: INVOICE_EVALUATIONS,
            permissions: [PermissionType.VIEW_INVOICE_EVALUATIONS],
            features: ['company/invoice-evaluations'],
          },
          {
            path: 'invoice-evaluations/:category_id',
            file: INVOICE_POSITION_EVALUATIONS,
            permissions: [PermissionType.VIEW_INVOICE_EVALUATIONS],
            features: ['company/invoice-evaluations'],
          },
          {
            path: 'product-mapping',
            file: PRODUCT_MAPPING,
            permissions: [PermissionType.EDIT_INVOICE_EVALUATIONS],
            features: ['company/product-mapping'],
          },
        ],
      },
      {
        path: 'invoice-receipt',
        file: INVOICE_RECEIPT,
        permissions: [PermissionType.EDIT_INVOICE_RECEIPT],
        features: ['invoice-receipt'],
      },
      {
        path: 'admin',
        file: ADMIN_PAGE_LAYOUT,
        children: [
          {
            path: 'projects',
            file: MANAGE_PROJECTS_VIEW,
            permissions: [PermissionType.EDIT_PROJECTS],
          },
          {
            path: 'project-regions',
            file: MANAGE_PROJECT_REGIONS_VIEW,
            permissions: [PermissionType.EDIT_REGIONS],
          },
          {
            path: 'users',
            file: MANAGE_USERS_PAGE,
            permissions: [PermissionType.EDIT_USERS],
          },
          {
            path: 'roles',
            file: MANAGE_ROLES_PAGE,
            permissions: [PermissionType.EDIT_ROLES],
          },
          {
            path: 'quality-control',
            file: QUALITY_CONTROL,
            permissions: [PermissionType.EDIT_SUPPLIERS],
          },
          {
            path: 'mappings',
            file: MAPPINGS_PAGE,
            permissions: [PermissionType.EDIT_MAPPINGS],
          },
          {
            path: 'invoice-settings',
            file: INVOICE_SETTINGS_PAGE,
            permissions: [PermissionType.EDIT_INVOICE_SETTINGS],
            features: ['accounting/invoice-settings'],
          },
          {
            path: 'suppliers',
            file: MANAGE_SUPPLIERS_PAGE,
            permissions: [PermissionType.EDIT_SUPPLIERS],
          },
          {
            path: 'suppliers/:supplier_id',
            params: ['supplier_id'],
            file: MANAGE_SUPPLIER_DETAILS_PAGE,
            permissions: [PermissionType.EDIT_SUPPLIERS],
          },
        ],
      },
      ...(isAdmin ? HOME_ROUTES_ADMIN : []),
    ],
  },
  {
    path: 'invoices/:invoice_id',
    file: INVOICE_EDIT_LAYOUT,
    params: ['invoice_id'],
    permissions: [PermissionType.EDIT_INVOICES],
    children: [
      {
        index: true,
        file: INVOICE_EDIT_WITH_URL,
      },
      {
        path: 'matching',
        file: INVOICE_MATCH,
      },
    ],
  },
]

/**
 * Hook to get the Home Routes based on the user roles
 * @remarks This approach will be removed when we remove the admin and bu_admin roles
 * @category Routes
 */
export function useHomeRoutes(): RouteDefs[] {
  const { isUserInRoles } = useUserContext()
  const isAdmin = isUserInRoles(['ADMIN'])
  return HOME_ROUTES(isAdmin)
}
